import * as React from 'react';
import { differenceInCalendarDays, sub } from 'date-fns';
import { useCubeQuery } from '@cubejs-client/react';

import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import {
    EActionMeasureType,
    EPromptMeasureType,
    EReviewsDimension,
    EReviewsMeasureType,
    EReviewsMeasureView,
    getActionAnalyticsFilters,
    getActionAnalyticsTimeDimensions,
    getReviewsAnalyticsFilters,
    getReviewsAnalyticsTimeDimensions,
    REVIEWS_DISTRIBUTION_MEASURES,
    REVIEWS_DISTRIBUTION_MEASURES_MAP,
    ReviewsDistributionMeasureKey,
    ReviewsMetricsTotal
} from '../reviewsAnalytics';

const COUNT_MEASURE = `${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.COUNT}`;
const TOTAL_RATING_MEASURE = `${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.TOTAL_RATING}`;
const FIVE_STAR_MEASURE = `${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.FIVE_STAR_COUNT}`;

function getSeriesValue(resultSet: any, measureKey: string): number | undefined {
    const series = resultSet?.series();
    if (!series) {
        return undefined;
    }
    const match = series.find((s: any) => s.key === measureKey);
    return match?.series[0]?.value;
}

export function useReviewsMetricsQueries(selectedLocationIds: string[], dateRange: DateRangePickerValue) {
    const retroDateRange = React.useMemo<[Date, Date]>(() => {
        const difference = differenceInCalendarDays(dateRange.endDate, dateRange.startDate);
        return [sub(dateRange.startDate, { days: difference + 1 }), sub(dateRange.startDate, { days: 1 })];
    }, [dateRange.endDate, dateRange.startDate]);

    // do not load retro data if we request more than a year
    const loadRetroData = React.useMemo(
        () => differenceInCalendarDays(retroDateRange[1], retroDateRange[0]) <= 366,
        [retroDateRange]
    );

    // Memoize shared filters and time dimensions to avoid unnecessary re-renders
    const reviewsFilters = React.useMemo(
        () => getReviewsAnalyticsFilters(EReviewsMeasureView.REVIEWS_METRICS, selectedLocationIds),
        [selectedLocationIds]
    );
    const currentTimeDimensions = React.useMemo(
        () => getReviewsAnalyticsTimeDimensions(EReviewsMeasureView.REVIEWS_METRICS, dateRange),
        [dateRange]
    );
    const retroTimeDimensions = React.useMemo(
        () =>
            getReviewsAnalyticsTimeDimensions(EReviewsMeasureView.REVIEWS_METRICS, {
                startDate: retroDateRange[0],
                endDate: retroDateRange[1]
            }),
        [retroDateRange]
    );

    // Combined current-period query: count, total_rating, five_star_count
    const currentMetricsQuery = useCubeQuery({
        measures: [COUNT_MEASURE, TOTAL_RATING_MEASURE, FIVE_STAR_MEASURE],
        dimensions: [],
        timeDimensions: currentTimeDimensions,
        filters: reviewsFilters
    });

    // Combined retro-period query (skipped when date range > 1 year)
    const retroMetricsQuery = useCubeQuery(
        {
            measures: [COUNT_MEASURE, TOTAL_RATING_MEASURE, FIVE_STAR_MEASURE],
            dimensions: [],
            timeDimensions: retroTimeDimensions,
            filters: reviewsFilters
        },
        { skip: !loadRetroData }
    );

    const ratingsCountValues = React.useMemo<ReviewsMetricsTotal>(() => {
        const currentRatingsCount = getSeriesValue(currentMetricsQuery.resultSet, COUNT_MEASURE);
        if (loadRetroData) {
            const retroRatingsCount = getSeriesValue(retroMetricsQuery.resultSet, COUNT_MEASURE);
            const difference =
                !currentRatingsCount || !retroRatingsCount
                    ? undefined
                    : ((currentRatingsCount || 0) - (retroRatingsCount || 0)) / (retroRatingsCount || 0);
            return {
                key: EReviewsMeasureType.COUNT,
                current: {
                    value: currentRatingsCount,
                    loading: currentMetricsQuery.isLoading
                },
                retro: {
                    value: retroRatingsCount,
                    loading: retroMetricsQuery.isLoading
                },
                difference
            };
        }
        return {
            key: EReviewsMeasureType.COUNT,
            current: {
                value: currentRatingsCount,
                loading: currentMetricsQuery.isLoading
            },
            retro: {
                value: 0,
                loading: false
            },
            difference: undefined
        };
    }, [currentMetricsQuery, retroMetricsQuery, loadRetroData]);

    // Average Rating is total_rating / total_count
    const averageRatingValues = React.useMemo<ReviewsMetricsTotal>(() => {
        const currentRatingsCount = getSeriesValue(currentMetricsQuery.resultSet, COUNT_MEASURE);
        const currentTotalRatings = getSeriesValue(currentMetricsQuery.resultSet, TOTAL_RATING_MEASURE);

        if (loadRetroData) {
            const retroRatingsCount = getSeriesValue(retroMetricsQuery.resultSet, COUNT_MEASURE);
            const retroTotalRatings = getSeriesValue(retroMetricsQuery.resultSet, TOTAL_RATING_MEASURE);
            const currentAvg =
                currentRatingsCount && currentTotalRatings ? currentTotalRatings / currentRatingsCount : 0;
            const retroAvg =
                retroRatingsCount && retroTotalRatings ? retroTotalRatings / retroRatingsCount : 0;
            const difference = !currentRatingsCount || !retroRatingsCount ? undefined : currentAvg - retroAvg;

            return {
                key: EReviewsMeasureType.AVG,
                current: {
                    value: currentAvg,
                    loading: currentMetricsQuery.isLoading
                },
                retro: {
                    value: retroAvg,
                    loading: retroMetricsQuery.isLoading
                },
                difference
            };
        }
        return {
            key: EReviewsMeasureType.AVG,
            current: {
                value: currentTotalRatings / currentRatingsCount,
                loading: currentMetricsQuery.isLoading
            },
            retro: {
                value: 0,
                loading: false
            },
            difference: undefined
        };
    }, [currentMetricsQuery, retroMetricsQuery, loadRetroData]);

    const totalFiveStarsValues = React.useMemo<ReviewsMetricsTotal>(() => {
        const currentFiveStars = getSeriesValue(currentMetricsQuery.resultSet, FIVE_STAR_MEASURE);
        if (loadRetroData) {
            const retroFiveStars = getSeriesValue(retroMetricsQuery.resultSet, FIVE_STAR_MEASURE);
            const difference =
                !currentFiveStars || !retroFiveStars
                    ? undefined
                    : ((currentFiveStars || 0) - (retroFiveStars || 0)) / (retroFiveStars || 0);
            return {
                key: EReviewsMeasureType.FIVE_STAR_COUNT,
                current: {
                    value: currentFiveStars,
                    loading: currentMetricsQuery.isLoading
                },
                retro: {
                    value: retroFiveStars,
                    loading: retroMetricsQuery.isLoading
                },
                difference
            };
        }
        return {
            key: EReviewsMeasureType.FIVE_STAR_COUNT,
            current: {
                value: currentFiveStars,
                loading: currentMetricsQuery.isLoading
            },
            retro: {
                value: 0,
                loading: false
            },
            difference: undefined
        };
    }, [currentMetricsQuery, retroMetricsQuery, loadRetroData]);

    const fiveStarPercentageValues = React.useMemo<ReviewsMetricsTotal>(() => {
        const currentFiveStars = getSeriesValue(currentMetricsQuery.resultSet, FIVE_STAR_MEASURE);
        const currentRatingsCount = getSeriesValue(currentMetricsQuery.resultSet, COUNT_MEASURE);
        return {
            key: EReviewsMeasureType.AVG,
            current: {
                value: currentFiveStars ? currentFiveStars / currentRatingsCount : 0,
                loading: currentMetricsQuery.isLoading
            },
            retro: {
                value: 0,
                loading: false
            },
            difference: undefined
        };
    }, [currentMetricsQuery]);

    const ratingDistributionQuery = useCubeQuery({
        measures: REVIEWS_DISTRIBUTION_MEASURES,
        dimensions: [],
        timeDimensions: currentTimeDimensions,
        filters: reviewsFilters
    });

    const ratingDistributionValues = React.useMemo(() => {
        const resultSeries = ratingDistributionQuery.resultSet?.series();
        if (!resultSeries) {
            return {
                distribution: [],
                loading: ratingDistributionQuery.isLoading
            };
        }
        const distribution: { rating: number; count: number }[] = resultSeries
            .map(series => {
                const key = series.key;
                return {
                    rating: REVIEWS_DISTRIBUTION_MEASURES_MAP[key as ReviewsDistributionMeasureKey].value,
                    count: series.series[0].value
                };
            })
            .sort((a, b) => b.rating - a.rating);
        return {
            distribution,
            loading: ratingDistributionQuery.isLoading
        };
    }, [ratingDistributionQuery]);

    const mobileRatingsCountQuery = useCubeQuery({
        measures: [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.COUNT}`],
        dimensions: [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsDimension.SOURCE_PLATFORM}`],
        timeDimensions: currentTimeDimensions,
        filters: getReviewsAnalyticsFilters(
            EReviewsMeasureView.REVIEWS_METRICS,
            selectedLocationIds,
            'mobile'
        )
    });

    const promptRatingsCountQuery = useCubeQuery({
        measures: [`${EReviewsMeasureView.PROMPT_METRICS}.${EPromptMeasureType.PROMPT_COUNT}`],
        dimensions: [],
        timeDimensions: getReviewsAnalyticsTimeDimensions(EReviewsMeasureView.PROMPT_METRICS, dateRange),
        filters: getReviewsAnalyticsFilters(EReviewsMeasureView.PROMPT_METRICS, selectedLocationIds)
    });

    const mobileEngagementData = React.useMemo(() => {
        if (
            !mobileRatingsCountQuery.resultSet?.series() ||
            !Array.isArray(promptRatingsCountQuery.resultSet?.series()) ||
            promptRatingsCountQuery.resultSet?.series().length === 0 ||
            !Array.isArray(mobileRatingsCountQuery.resultSet?.series())
        ) {
            return {
                engagement: 0,
                dismissals: 0,
                loading: mobileRatingsCountQuery.isLoading || promptRatingsCountQuery.isLoading,
                totalRatingsCount: 0
            };
        }
        const mobileRatingsCount = mobileRatingsCountQuery.resultSet?.series()[0]?.series[0]?.value ?? 0;
        const promptRatingsCount = promptRatingsCountQuery.resultSet?.series()[0]?.series[0]?.value ?? 0;
        const engagement = mobileRatingsCount ? mobileRatingsCount / promptRatingsCount : 0;
        const dismissals = promptRatingsCount - mobileRatingsCount;
        return {
            engagement,
            dismissals,
            loading: mobileRatingsCountQuery.isLoading || promptRatingsCountQuery.isLoading,
            totalRatingsCount: mobileRatingsCount
        };
    }, [mobileRatingsCountQuery, promptRatingsCountQuery]);

    const webOrdersQuery = useCubeQuery({
        measures: [`${EReviewsMeasureView.ACTION_METRICS}.${EActionMeasureType.ORDER_COUNT}`],
        dimensions: [],
        filters: getActionAnalyticsFilters(selectedLocationIds, 'web'),
        timeDimensions: getActionAnalyticsTimeDimensions(dateRange)
    });

    const totalWebReviewsQuery = useCubeQuery({
        measures: [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.COUNT}`],
        dimensions: [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsDimension.SOURCE_PLATFORM}`],
        filters: getReviewsAnalyticsFilters(EReviewsMeasureView.REVIEWS_METRICS, selectedLocationIds, 'web'),
        timeDimensions: currentTimeDimensions
    });

    const webEngagementData = React.useMemo(() => {
        if (!webOrdersQuery.resultSet?.series() || !totalWebReviewsQuery.resultSet?.series()) {
            return {
                engagement: 0,
                totalRatings: 0,
                totalOrders: 0,
                loading: webOrdersQuery.isLoading || totalWebReviewsQuery.isLoading
            };
        }
        const totalRatings = totalWebReviewsQuery.resultSet?.series()[0]?.series[0]?.value ?? 0;
        const totalOrders = webOrdersQuery.resultSet?.series()[0]?.series[0]?.value ?? 0;
        const engagement = totalOrders ? totalRatings / totalOrders : 0;
        return {
            engagement,
            totalRatings,
            totalOrders,
            loading: webOrdersQuery.isLoading || totalWebReviewsQuery.isLoading
        };
    }, [webOrdersQuery, totalWebReviewsQuery]);

    return {
        ratingsCountValues,
        averageRatingValues,
        totalFiveStarsValues,
        fiveStarPercentageValues,
        ratingDistributionValues,
        mobileEngagementData,
        webEngagementData
    };
}
